import { useState } from "react";
import { fmtInt, fmtMoney, fmtPct, fmtNum } from "./format";

const CRITERIOS = [
  { k: "unidades", l: "Unidades", sort: (a, b) => (b.unidades_0 || 0) - (a.unidades_0 || 0), col: "unidades_0", f: fmtNum },
  { k: "monto", l: "Monto", sort: (a, b) => (b.monto_0 || 0) - (a.monto_0 || 0), col: "monto_0", f: (v) => fmtMoney(v) },
  { k: "crecimiento", l: "Crecimiento", sort: (a, b) => (b.var_pct || 0) - (a.var_pct || 0), col: "var_pct", f: fmtPct, filter: (r) => (r.var_pct || 0) > 0, pct: true },
  { k: "caida", l: "Mayor caída", sort: (a, b) => (a.var_pct || 0) - (b.var_pct || 0), col: "var_pct", f: fmtPct, filter: (r) => (r.var_pct || 0) < 0, pct: true },
  { k: "facturas", l: "Cantidad de facturas", sort: (a, b) => (b.facturas || 0) - (a.facturas || 0), col: "facturas", f: fmtInt },
];

/** Vista ANÁLISIS: rankings Top 5/10/20/Todos + crecimiento/caída + sin compra / nuevos / reactivados. */
export default function AnalisisPanel({ rows, hasComp, onOpen, dimLabel }) {
  const [crit, setCrit] = useState("unidades");
  const [topN, setTopN] = useState(10);
  const c = CRITERIOS.find((x) => x.k === crit);
  let ranked = [...rows];
  if (c.filter) ranked = ranked.filter(c.filter);
  ranked.sort(c.sort);
  if (topN) ranked = ranked.slice(0, topN);
  const by = (k) => rows.filter((r) => r.comportamiento === k);
  const caidas = [...rows].filter((r) => (r.var_pct || 0) < 0).sort((a, b) => (a.var_pct || 0) - (b.var_pct || 0)).slice(0, topN || undefined);

  return (
    <div className="p-4 space-y-4" data-testid="view-analisis">
      <div className="bg-app/40 border border-border/40 rounded-lg overflow-hidden" id="rank-main" data-testid="rank-main">
        <div className="px-3 py-2 border-b border-border/40 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-agro-green font-semibold mr-2">Ranking de {dimLabel.toLowerCase()}</span>
          {CRITERIOS.filter((x) => hasComp || !x.pct).map((x) => (
            <button key={x.k} onClick={() => setCrit(x.k)} data-testid={`rank-crit-${x.k}`}
              className={`text-[10px] rounded px-2 py-0.5 border ${crit === x.k ? "border-agro-green text-agro-green" : "border-border text-text-muted"}`}>{x.l}</button>
          ))}
          <select value={topN} onChange={(e) => setTopN(Number(e.target.value))} data-testid="rank-topn"
            className="ml-auto bg-app border border-border rounded px-2 py-1 text-[11px]">
            <option value={5}>Top 5</option><option value={10}>Top 10</option>
            <option value={20}>Top 20</option><option value={0}>Todos</option>
          </select>
        </div>
        <List rows={ranked} col={c.col} fmt={c.f} pct={c.pct} onOpen={onOpen} />
      </div>

      {hasComp ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Block title="Mayores caídas" id="list-caidas" rows={caidas} col="var_pct" fmt={fmtPct} pct onOpen={onOpen} />
          <Block title="Sin compra · CLIENTE EN RIESGO" id="list-sin-compra" rows={by("sin_compra")} tone="danger"
            col="unidades_1" fmt={(v) => `Antes: ${fmtNum(v)}`} onOpen={onOpen} />
          <Block title="En crecimiento" id="list-crecimiento" rows={by("crecimiento")} col="var_pct" fmt={fmtPct} pct onOpen={onOpen} />
          <Block title="En caída" id="list-caida" rows={by("caida")} col="var_pct" fmt={fmtPct} pct onOpen={onOpen} />
          <Block title="Nuevos" id="list-nuevos" rows={by("nuevo")} tone="new" col="unidades_0" fmt={fmtNum} onOpen={onOpen} />
          <Block title="Reactivados" id="list-reactivados" rows={by("reactivado")} tone="new" col="unidades_0" fmt={fmtNum} onOpen={onOpen} />
        </div>
      ) : (
        <div className="text-xs text-text-muted p-3 border border-border/40 rounded">
          Selecciona “Comparar vs” (1 o más períodos anteriores) para ver crecimiento, caídas, sin compra, nuevos y reactivados.
        </div>
      )}
      <p className="text-[10px] text-text-muted">
        Criterio: <b>Sin compra</b> = compró en el período comparable anterior y 0 ahora. <b>Nuevo</b> = compra ahora y no tiene ninguna
        venta antes del inicio del rango. <b>Reactivado</b> = compra ahora, no compró en el período comparable anterior (o en los meses de
        inactividad indicados) y sí tenía historial antes.
      </p>
    </div>
  );
}

function List({ rows, col, fmt, pct, onOpen }) {
  if (!rows.length) return <div className="p-4 text-center text-text-muted text-xs">Sin datos</div>;
  return (
    <div className="divide-y divide-border/20 max-h-[360px] overflow-y-auto">
      {rows.map((r, i) => (
        <button key={r.grupo_id || i} onClick={() => onOpen(r)}
          className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-white/[0.03] text-left">
          <span className="text-xs flex items-center gap-2 min-w-0">
            <span className="text-[10px] text-text-muted w-5">{i + 1}</span>
            <span className="truncate">{r.grupo}</span>
          </span>
          <span className={`text-xs font-mono ${pct ? ((r[col] || 0) < 0 ? "text-red-400" : "text-emerald-400") : "text-white"}`}>
            {fmt(r[col])}
          </span>
        </button>
      ))}
    </div>
  );
}

function Block({ title, id, rows, tone, col, fmt, pct, onOpen }) {
  const badge = tone === "danger" ? "text-red-400 bg-red-500/10" : tone === "new" ? "text-sky-400 bg-sky-500/10" : "text-text-muted bg-white/5";
  return (
    <div className="bg-app/40 border border-border/40 rounded-lg overflow-hidden" id={id} data-testid={id}>
      <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-agro-green font-semibold">{title}</span>
        <span className={`text-[10px] rounded px-1.5 py-0.5 ${badge}`}>{rows.length}</span>
      </div>
      <List rows={rows} col={col} fmt={fmt} pct={pct} onOpen={onOpen} />
    </div>
  );
}
